import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Center, Paper, Stack, Text, TextInput, PasswordInput, Button, Alert, Anchor, Group } from '@mantine/core';
import { AlertCircle, CircleCheck } from 'lucide-react';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import { UltraTraceWordmark } from '../../design/UltraTraceLogo';

/** Shortest password we accept — must match the auth-api MIN_PASSWORD_LENGTH. */
const MIN_PASSWORD_LENGTH = 8;

interface SetPasswordPageProps {
    /** `invite` sets up a new account (name + password); `reset` replaces a forgotten password. */
    mode: 'invite' | 'reset';
}

/**
 * Public (pre-login) page that redeems a signed action link on our own branded
 * surface — no Keycloak-hosted page. In `invite` mode it collects the user's name
 * and password to finish setting up the account; in `reset` mode it just replaces
 * the password. The token from the emailed link is the credential, posted to
 * `/api/auth/set-password` which drives the Keycloak admin API server-side.
 */
export function SetPasswordPage({ mode }: SetPasswordPageProps): React.ReactElement {
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const isInvite = mode === 'invite';
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const heading = isInvite ? 'Set up your account' : 'Reset your password';
    const intro = isInvite
        ? 'Enter your name and choose a password to finish setting up your Ultra Trace account.'
        : 'Choose a new password for your Ultra Trace account.';

    async function submit(): Promise<void> {
        if (isInvite && (!firstName.trim() || !lastName.trim())) {
            setError('Please enter your first and last name.');
            return;
        }
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const res = await authenticatedFetch('/_auth/api/auth/set-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    password,
                    ...(isInvite ? { firstName: firstName.trim(), lastName: lastName.trim() } : {}),
                }),
            });
            if (!res.ok) {
                const body = (await res.json().catch(() => ({}))) as { error?: string };
                setError(body.error ?? 'Could not set your password. Please request a new link.');
                setBusy(false);
                return;
            }
            setDone(true);
        } catch {
            setError('Something went wrong. Please try again.');
            setBusy(false);
        }
    }

    return (
        <Center h="100dvh" p="md">
            <Paper shadow="md" p="xl" radius="md" withBorder w="100%" maw={420}>
                <Stack>
                    <Center>
                        <UltraTraceWordmark size={40} title="Ultra Trace" />
                    </Center>

                    {!token ? (
                        <Alert variant="light" color="red" icon={<AlertCircle size={16} />}>
                            This link is invalid or has expired. Please request a new one.
                        </Alert>
                    ) : done ? (
                        <Stack align="center" gap="sm">
                            <CircleCheck size={40} color="var(--mantine-color-teal-6)" />
                            <Text ta="center">Your account is ready. You can now sign in.</Text>
                            <Anchor href="/">Go to sign in</Anchor>
                        </Stack>
                    ) : (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void submit();
                            }}
                        >
                            <Stack>
                                <Text c="dimmed" size="sm" ta="center">
                                    {intro}
                                </Text>
                                {error && (
                                    <Alert variant="light" color="red" icon={<AlertCircle size={16} />}>
                                        {error}
                                    </Alert>
                                )}
                                {isInvite && (
                                    <Group grow>
                                        <TextInput
                                            label="First name"
                                            autoComplete="given-name"
                                            value={firstName}
                                            onChange={(e) => {
                                                setFirstName(e.currentTarget.value);
                                                setError('');
                                            }}
                                            autoFocus
                                        />
                                        <TextInput
                                            label="Last name"
                                            autoComplete="family-name"
                                            value={lastName}
                                            onChange={(e) => {
                                                setLastName(e.currentTarget.value);
                                                setError('');
                                            }}
                                        />
                                    </Group>
                                )}
                                <PasswordInput
                                    label="Password"
                                    placeholder="At least 8 characters"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.currentTarget.value);
                                        setError('');
                                    }}
                                    autoFocus={!isInvite}
                                />
                                <PasswordInput
                                    label="Confirm password"
                                    placeholder="Re-enter your password"
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(e) => {
                                        setConfirm(e.currentTarget.value);
                                        setError('');
                                    }}
                                />
                                <Button type="submit" fullWidth loading={busy}>
                                    {heading}
                                </Button>
                            </Stack>
                        </form>
                    )}
                </Stack>
            </Paper>
        </Center>
    );
}

export default SetPasswordPage;
